import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-balham.css';
import classNames from 'classnames';
import { CREATE_A_CONTAINER } from './screen';
import {
  TableHeader,
  TableContent,
  usePreventTableScrollingRef,
  TableContainer,
  CELL_RENDERERS,
  TextRenderer,
  IconRenderer,
  RatingRenderer,
  ROW_SELECTION,
  classNamePrefix,
  IconInfo,
  IconDelete,
  TextButton,
  withTooltip,
  THEMES,
  TOOLTIP_HORIZONTAL_ALIGNMENTS,
  TOOLTIP_VERTICAL_ALIGNMENTS
} from 'cdk-radial';

const IconInfoWithTooltip = withTooltip(IconInfo, {
  id: `fdn-endpoint-container__info_icon-tooltip`,
  text:
    'Any endpoint not assigned to a container will be assigned to non-billable container',
  theme: THEMES.LIGHT
});

export default function EndpointContainer({
  pricingPlan,
  containers,
  setScreen,
  setSelectedSubPlanContainer,
  bundleApiEndpoints,
  bundleApiChannels,
  onRemoveBucket,
  onEditContainer
}) {
  const componentMainClass = `${classNamePrefix}-table`;
  const agGridThemeClass = 'ag-theme-balham';
  const tableClass = classNames(componentMainClass, agGridThemeClass, 'endpoint-container-custom-table');
  const [gridApi, setGridApi] = useState(null);
  const [containersData, setContainersData] = useState([]);

  useEffect(() => {
    if (!!containers) {
      // Format number to have commas
      const formatNumber = (num) => {
        if (!num && num !== 0) return "";
        return String(num).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      };

      const formatCurrency = (val) => {
        if (!val && val !== 0) return "";
        const num = parseFloat(String(val).replace(/,/g, ""));
        if (isNaN(num)) return val;
        return `$${num.toFixed(2)}`;
      };

      const containersData = containers.map(container => ({
        ...container,
        quotaLimit: formatNumber(container.quotaLimit),
        overages: formatCurrency(container.overages),
        endpoints: parseInt(container.endpoints?.length || 0) + parseInt(container.channels?.length || 0)
      }));

      // Calculate Non-Billable Endpoints (endpoints not in any container)
      if (bundleApiEndpoints && bundleApiChannels) {
        const totalEndpoints = Object.keys(bundleApiEndpoints).length + Object.keys(bundleApiChannels).length;

        // Count unique endpoints and channels used across all containers
        const usedEndpointIds = new Set();
        const usedChannelIds = new Set();

        containers.forEach(container => {
          (container.endpoints || []).forEach(ep => usedEndpointIds.add(ep.endpointId));
          (container.channels || []).forEach(ch => usedChannelIds.add(ch.id));
        });

        const usedCount = usedEndpointIds.size + usedChannelIds.size;
        const unassignedCount = Math.max(0, totalEndpoints - usedCount);

        // Add Non-Billable Endpoints row
        containersData.push({
          name: 'Non-Billable Endpoints',
          quotaLimit: 'Unlimited',
          overages: '$0.00',
          endpoints: unassignedCount,
          isFree: true,
        });
      }

      setContainersData(containersData);
    }
  }, [containers, bundleApiEndpoints, bundleApiChannels]);

  const editActionRenderer = ({ data }) => {
    const handleEditClick = () => {
      if (onEditContainer) {
        onEditContainer(data.name);
      } else {
        if (setSelectedSubPlanContainer) {
          setSelectedSubPlanContainer(data.name);
        }
        setScreen(CREATE_A_CONTAINER);
      }
    };

    return (
      <div className="endpoint-container-action-cell-edit">
        {data.isFree ? (
          <IconInfoWithTooltip
            horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
            verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.TOP}
            className="action-icon-grey"
          />
        ) : (
          <TextButton
            dataTestId="datatest-edit-bucket-row"
            iconColor="primary"
            onClick={handleEditClick}
            text="Edit"
          />
        )}
      </div>
    );
  };

  const deleteActionRenderer = ({ data }) => {
    const handleDeleteClick = () => {
      onRemoveBucket(data);
    };

    return (
      <div className="endpoint-container-action-cell-delete">
        {!data.isFree && (
          <IconDelete id='icon-delete' className="action-icon-grey" onClick={handleDeleteClick} />
        )}
      </div>
    );
  };

  const columns = [
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'name',
      headerName: 'Name',
      width: 180,
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'quotaLimit',
      headerName: 'Monthly Quota',
      width: 140,
      headerClass: 'ec-header-cell-right',
      cellClass: 'ec-cell-right',
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'overages',
      headerName: 'Overages',
      width: 100,
      headerClass: 'ec-header-cell-right',
      cellClass: 'ec-cell-right',
    },
    {
      cellRenderer: CELL_RENDERERS.TEXT,
      field: 'endpoints',
      headerName: 'Endpoints',
      width: 100,
      headerClass: 'ec-header-cell-right',
      cellClass: 'ec-cell-right',
    },
    {
      cellRendererFramework: editActionRenderer,
      field: '',
      headerName: '',
      width: 60,
      suppressSizeToFit: true,
    },
    {
      cellRendererFramework: deleteActionRenderer,
      field: '',
      headerName: '',
      width: 50,
      suppressSizeToFit: true,
    },
  ];

  const tableContentRef = usePreventTableScrollingRef();
  const frameworkComponentsExtended = {
    textRenderer: TextRenderer,
    iconRenderer: IconRenderer,
    ratingRenderer: RatingRenderer
  };

  const handleGridReady = gridParams => {
    setGridApi(gridParams.api);
    gridParams.api.sizeColumnsToFit();
  };

  const formattedTitle = pricingPlan?.title
    ? pricingPlan.title.charAt(0).toUpperCase() + pricingPlan.title.slice(1)
    : '';

  const headerProps = {
    dataTestId: `datatest-endpoint-container`,
    headerTitle: formattedTitle ? `${formattedTitle} Endpoint Containers` : 'Endpoint Containers',
    hideBorder: true,
    id: `endpoint-container`,
    isDownloadable: false,
    isPrintable: false,
    isSearchable: false
  };
  return (
    <TableContainer
      className={tableClass}
      style={{
        width: '50.22vw',
        maxWidth: '100%',
      }}
      tableLayout={'normal'}
    >
      <TableHeader {...headerProps} />
      <TableContent data-testid="table-content" ref={tableContentRef}>
        <AgGridReact
          defaultColDef={{
            filter: false,
            sortable: false,
            resizable: false,
          }}
          frameworkComponents={frameworkComponentsExtended}
          columnDefs={columns}
          onGridReady={handleGridReady}
          rowData={containersData}
          rowSelection={ROW_SELECTION.SINGLE}
          rowHeight={44}
          headerHeight={32}
          domLayout="autoHeight"
          suppressRowClickSelection={true}
          getRowStyle={(params) => ({
            background: '#FFFFFF',
            boxShadow: 'inset 0px -1px 0px #DCDDDE',
          })}
        />
      </TableContent>
    </TableContainer>
  );
}
